import styled from '@emotion/styled';
import { breakpointToContentSlotPadding } from 'ds/components/Layout';
import { SmallText, Text } from 'ds/components/typography';
import { InsightType } from 'utils/entities';
import { BreakpointValue } from 'consts/breakpoints';
import { SpacingScale } from 'ds/theme/spacing';


export const AreaHighlightHeader = styled.div`
  display: flex;
  align-items: center;
  margin: 0 -${({ theme }) => theme.spacing(1)};
  svg {
    margin: 0 ${({ theme }) => theme.spacing(1)};
  }
  ${({ theme }) => theme.media.showAt({ at: 1 })`
    flex-direction: row-reverse;
    justify-content: space-between;
  `}
`;

export const AreaHighlightHeaderTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 ${({ theme }) => theme.spacing(1.5)};
  div {
    color: ${({ theme }) => theme.colors.neutrals.grey3};
  }
  h2 {
    color: ${({ theme }) => theme.colors.neutrals.grey1}
  }
`;

export const AreaHighlightCarouselsWrapper = styled.div<{ hidePadding?: boolean }>`
  display: flex;
  flex-direction: column;
  margin-top: ${({ theme, hidePadding= false }) => hidePadding ? theme.spacing(8) : 0};
  padding-top: ${({ theme, hidePadding= false }) => !hidePadding ? theme.spacing(3) : 0};
  ${({ theme, hidePadding= false }) => !hidePadding && theme.media.showAt({ at: 1 })`
    padding-top: ${theme.spacing(1)};
  `}
  padding-bottom: ${({ theme, hidePadding }) => !hidePadding ? theme.spacing(5) : 0};
`;

export const AreaHighlightCarouselHeader = styled(Text)`
  margin-bottom: ${({ theme }) => theme.spacing(1)};
  color: ${({ theme }) => theme.colors.neutrals.grey2};
`;

export const CarouselWrapper = styled.div<{hidePadding?: boolean}>`
  display: flex;
  flex-direction: column;
  margin-top: ${({ theme, hidePadding= false }) => !hidePadding && theme.spacing(5)};
  margin-bottom: ${({ theme, hidePadding= false }) => !hidePadding ? `-${theme.spacing(2)}` : 0};
`;

export const AreaHighlightCardBox = styled.div<{insightType: InsightType}>`
  position: relative;
  transform: scale(1);
  transition: all 300ms;

  ${({ theme, insightType }) => theme.media.showAt({ from: 3 })`
    &:hover {
      transform: scale(1.02);
      > div:last-of-type {
        box-shadow: ${theme.shadow.level5};
      }
      .area-read-more {
        opacity: 1;
        color: ${theme.colors.insights[insightType]};

        svg {
          path {
            fill: ${theme.colors.insights[insightType]};
          }
        }
      }
    }
  `}

  > div:first-of-type {
    position: absolute;
    top: ${({ theme }) => theme.spacing(0.5)};
    ${({ theme }) => `${theme.isRTL ? 'left' : 'right'}: ${theme.spacing(5)};`}
    z-index: 1;
  }
`;

export const AreaHighlightCardWrapper = styled.div<{insightType: InsightType}>`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  margin: ${({ theme }) => theme.spacing(1)} 0 ${({ theme }) => theme.spacing(2)} 0;
  background: ${({ theme }) => theme.colors.neutrals.white};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  padding: ${({ theme }) => theme.spacing(2)};
  box-shadow: ${({ theme }) => theme.shadow.level2};
  height: 156px;
  margin-${({ theme }) => `${theme.isRTL ? 'left' : 'right'}: ${theme.spacing(3)}`};
  cursor: pointer;

  ${({ theme }) => theme.media.showAt({ from: 2, to: 5 })`
    width: 261px;
  `}

  ${({ theme }) => theme.media.showAt({ at: 1 })`
    width: 230px;
  `}

  ${({ theme }) => theme.media.showAt({ to: 2 })`
    height: 180px;
  `}
`;

export const AreaMainInfoWrapper = styled.div`
  display: flex;
  line-height: 1.41;
  font-size: 17px;
`;

export const AreaReadMore = styled(SmallText)`
  position: absolute;
  bottom: ${({ theme }) => theme.spacing(2)};
  ${({ theme }) => theme.isRTL ? 'right' : 'left'}: ${({ theme }) => theme.spacing(2)};
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.neutrals.grey4};
  cursor: pointer;
  opacity: 0;
  transition: all 300ms;

  svg {
    transform: rotate(${({ theme }) => theme.isRTL ? '180deg' : '0'});
    margin-${({ theme }) => `${theme.isRTL ? 'right' : 'left'}: ${theme.spacing(0.5)}`};
      path {
      fill: ${({ theme }) => theme.colors.neutrals.grey4};
    }
  }

  ${({ theme }) => theme.media.showAt({ to: 2 })`
    opacity: 1;
  `}
`;

export const AreaHighlightsCarouselWrapper = styled.div<{oldStyle?: boolean, isWhiteBg?: boolean }>`
  display: flex;
  user-select: none;
  ${({ theme, oldStyle = false, isWhiteBg }) => Object.entries(breakpointToContentSlotPadding).map(([ range, padding ]) =>
  theme.media.showAt({ at: Number(range) as BreakpointValue })`
      ${!oldStyle ? `
        div.view-pager-rail {
          padding: 0 ${theme.spacing(padding)};
        }

        .left-slide-btn,
        .right-slide-btn {
          width: ${theme.spacing(padding * 2 as SpacingScale)};
        }
      ` : ''}

      .left-slide-btn {
        ${theme.isRTL ? 'right' : 'left'}: 0;
        background: linear-gradient(to ${theme.isRTL ? 'left' : 'right'}, ${isWhiteBg ? theme.colors.neutrals.white : theme.colors.neutrals.grey9} 20%, ${theme.colors.neutrals.transparent});
         > div {
          ${!oldStyle ? `${theme.isRTL ? 'right' : 'left'}: ${theme.spacing(padding)}` : ''};
        }
      }

      .right-slide-btn {
        ${theme.isRTL ? 'left' : 'right'}: 0;
        background: linear-gradient(to ${theme.isRTL ? 'right' : 'left'}, ${isWhiteBg ? theme.colors.neutrals.white : theme.colors.neutrals.grey9} 20%, ${theme.colors.neutrals.transparent});
         > div {
          ${!oldStyle ? `${theme.isRTL ? 'left' : 'right'}: ${theme.spacing(padding)}` : ''};
        }
      }
    `)}

  .left-slide-btn,
  .right-slide-btn {
    position: absolute;
    top: 0;
    height: 100%;
    > div {
      top: calc(50% - 16px);
    }
  }

  div.view-pager-bow {
    > div:first-of-type {
    margin-${({ theme }) => theme.isRTL ? 'right' : 'left'}: ${({ theme }) => theme.spacing(0.5)};
  }
  }

  .slide-btn {
    visibility: hidden;
  }

  > div {
    width: 100%;
    margin-top: 0;
  }

  ${({ theme }) => theme.media.showAt({ from: 2 })`
    &:hover {
      .slide-btn {
        visibility: visible;
      }
    }
  `}
`;

export const EmptyInsightsSection = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 112px;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  background-color: ${({ theme }) => theme.colors.neutrals.grey10};
  padding: 0 ${({ theme }) => theme.spacing(4)};
  color: ${({ theme }) => theme.colors.neutrals.grey2};

  ${({ theme }) => theme.media.showAt({ at: 1 }) `
    height: 72px;
    padding: 0 ${theme.spacing(2)};
  `}
`;

export const SectionHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(3)};
  ${({ theme }) => theme.media.showAt({ at: 1 })`
    margin-bottom: ${theme.spacing(2)};
  `}
`;
